import {
  Check,
  LocalOffer,
  PlayArrow,
  Stop,
} from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  InputAdornment,
  InputBase,
  Switch,
  TextField,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import BackdropLoading from "components/BackdropLoading";
import { useNavigation } from "contexts/notificationsContext";
import { isEqual } from "lodash";
import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { useAuth } from "shared/hooks/auth";
import {
  atualizarTarefa,
  atualizarSubtarefa,
  criarOuObterTag,
  criarSubtarefa,
  duplicarTarefa,
  excluirTarefa,
  excluirSubtarefa,
  IAtualizarTarefaRequest,
  IAtualizarSubtarefaRequest,
  ICriarSubtarefaDetalheRequest,
  ITag,
  ITarefaCompleta,
  listarTags,
  obterTarefaCompleta,
  obterSubtarefaCompleta,
} from "services/modules/tarefaDetalhe";
import {
  criarTarefa,
  ICriarTarefaRequest,
  iniciarTimer,
  pararTimer,
  getTimerAtivo,
  ITimerAtivo,
} from "services/modules/quadroTarefas";
import { TabDetalhes } from "./TabDetalhes";
import { TabApontamentoHoras } from "./TabApontamentoHoras";
import { TabDocumentos } from "./TabDocumentos";
import { TabAtividades } from "./TabAtividades";
import { PainelComentarios } from "./PainelComentarios";
import { PainelSubtarefas } from "./PainelSubtarefas";
import { ModalConfirmarSubtarefas } from "./Modais/ModalConfirmarSubtarefas";
import { getGatilhoPorTipoAtividade, IEmpresaTarefaGatilho } from "services/modules/tarefas";
import { normalizeFormData } from "./utils/dirtyState";

// ============================================
// STYLED COMPONENTS - Conforme Mockup
// ============================================

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(2, 3),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: "#fff",
}));

const TarefaNumeroContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  marginRight: theme.spacing(2),
}));

const TarefaNumero = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 600,
  fontSize: "1rem",
  border: `1px solid ${theme.palette.primary.main}`,
  padding: theme.spacing(0.5, 1.5),
  borderRadius: 4,
  backgroundColor: "#fff",
}));

const BotoesAcaoContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  marginTop: theme.spacing(1),
}));

const TituloInput = styled(InputBase)(({ theme }) => ({
  flex: 1,
  fontSize: "1rem",
  fontWeight: 500,
  padding: theme.spacing(0.5, 1),
  borderRadius: 4,
  border: "1px solid transparent",
  "&:hover": {
    borderColor: theme.palette.divider,
  },
  "&.Mui-focused": {
    borderColor: theme.palette.primary.main,
    backgroundColor: "#fff",
  },
}));

const ActionButton = styled(IconButton)<{ bgcolor?: string }>(({ bgcolor }) => ({
  backgroundColor: bgcolor || "#e0e0e0",
  color: "#fff !important", // Forçar cor branca para o ícone
  marginLeft: 8,
  width: 36,
  height: 36,
  "& svg": {
    color: "#fff !important", // Garantir que o ícone seja branco
  },
  "&:hover": {
    backgroundColor: bgcolor ? `${bgcolor}dd` : "#bdbdbd",
  },
  "&:disabled": {
    backgroundColor: bgcolor ? `${bgcolor}80` : "#bdbdbd",
    color: "#fff !important",
    "& svg": {
      color: "#fff !important",
    },
  },
}));

const TarefaInternaToggle = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  backgroundColor: "#f5f5f5",
  padding: theme.spacing(0.5, 1.5),
  borderRadius: 20,
  marginLeft: theme.spacing(2),
}));

const PainelLateral = styled(Box)(({ theme }) => ({
  width: 380,
  display: "flex",
  flexDirection: "column",
  borderLeft: `1px solid ${theme.palette.divider}`,
  backgroundColor: "#fafafa",
}));

// Abas do painel lateral (LISTA DE TAREFAS / COMENTÁRIOS) - conforme mockup
const PainelLateralTabsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: 0,
  padding: theme.spacing(1),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: "#fff",
}));

const PainelLateralTab = styled(Button, {
  shouldForwardProp: (prop) => prop !== "active",
})<{ active?: boolean }>(({ theme, active }) => ({
  flex: 1,
  borderRadius: 4,
  padding: theme.spacing(0.75, 1),
  fontSize: "0.7rem",
  fontWeight: "bold",
  textTransform: "uppercase",
  whiteSpace: "nowrap",
  backgroundColor: active ? "#3C90AD" : "#e0e0e0",
  color: active ? "#fff" : "#666",
  border: "none",
  "&:hover": {
    backgroundColor: active ? "#357a94" : "#d0d0d0",
  },
}));

// Container da linha Tags + Abas (conforme mockup)
const TagsAndTabsRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(1, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: "#fff",
}));

// Abas estilizadas como botões (conforme mockup)
const TabsButtonGroup = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: 0,
}));

interface StyledTabButtonProps {
  active?: boolean;
}

const TabButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "active",
})<StyledTabButtonProps>(({ theme, active }) => ({
  backgroundColor: active ? "#3C90AD" : "#5BA8C0",
  color: "#fff",
  borderRadius: 0,
  padding: theme.spacing(1, 2),
  fontSize: "0.75rem",
  fontWeight: 600,
  textTransform: "uppercase",
  minHeight: 36,
  "&:hover": {
    backgroundColor: active ? "#3C90AD" : "#4A9BB3",
  },
  "&:first-of-type": {
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
  },
  "&:last-of-type": {
    borderTopRightRadius: 4,
    borderBottomRightRadius: 4,
  },
}));

// ============================================
// TAB PANEL
// ============================================

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tarefa-tabpanel-${index}`}
      aria-labelledby={`tarefa-tab-${index}`}
      {...other}
      style={{ height: "100%", overflow: "auto" }}
    >
      {value === index && <Box sx={{ p: 2, height: "100%" }}>{children}</Box>}
    </div>
  );
}

// ============================================
// INTERFACES
// ============================================

interface ModalTarefaDetalheProps {
  open: boolean;
  idTarefa: number | null;
  onClose: () => void;
  onSave: () => void;
  isSubtarefa?: boolean; // Flag para indicar se deve carregar como sub-tarefa
}

// ============================================
// COMPONENTE PRINCIPAL
// ============================================

export const ModalTarefaDetalhe = ({
  open,
  idTarefa,
  onClose,
  onSave,
  isSubtarefa = false,
}: ModalTarefaDetalheProps) => {
  const { user, havePermission } = useAuth();
  const { openNotification } = useNavigation();

  // Estados principais
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [tarefa, setTarefa] = useState<ITarefaCompleta | null>(null);
  const [tabAtual, setTabAtual] = useState(0);
  const [tabPainelLateral, setTabPainelLateral] = useState(0); // 0 = Lista de Tarefas, 1 = Comentários
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState<Partial<IAtualizarTarefaRequest>>({});
  const [hasChanges, setHasChanges] = useState(false);
  // Id da tarefa recém-criada — mantém o modal aberto em modo edição após criar (item 6.1)
  const [idTarefaCriada, setIdTarefaCriada] = useState<number | undefined>(undefined);

  // Snapshot do formulário ao carregar/criar — base para detectar alterações reais
  const initialFormDataRef = useRef<Partial<IAtualizarTarefaRequest>>({});
  const initialTagsRef = useRef<number[]>([]);

  // Estados do timer
  const [timerAtivo, setTimerAtivo] = useState<ITimerAtivo | null>(null);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [apontamentoRefreshKey, setApontamentoRefreshKey] = useState(0);

  // Estados para Tags (conforme mockup - na linha das abas)
  const [tags, setTags] = useState<ITag[]>([]);
  const [tagsSelecionadas, setTagsSelecionadas] = useState<ITag[]>([]);
  const [tagInputValue, setTagInputValue] = useState("");
  const [criandoTag, setCriandoTag] = useState(false);

  // Estados para modal de confirmação de sub-tarefas
  const [modalConfirmarSubtarefasOpen, setModalConfirmarSubtarefasOpen] = useState(false);
  const [gatilhoEncontrado, setGatilhoEncontrado] = useState<IEmpresaTarefaGatilho | null>(null);
  const [pendingSave, setPendingSave] = useState<(() => Promise<void>) | null>(null);

  // Estados para modal de sub-tarefa (abrir sub-tarefa em modal separada)
  const [modalSubtarefaAberto, setModalSubtarefaAberto] = useState(false);
  const [subtarefaSelecionadaId, setSubtarefaSelecionadaId] = useState<number | null>(null);

  // Quando for sub-tarefa, forçar aba de Comentários (sub-tarefas não têm sub-tarefas aninhadas)
  useEffect(() => {
    if (isSubtarefa) {
      setTabPainelLateral(1); // 1 = Comentários
    }
  }, [isSubtarefa]);

  const idEmpresa = user?.idEmpresa || 0;
  // Id efetivo: após criar (item 6.1), o modal migra para modo edição sem fechar,
  // usando o id retornado (idTarefaCriada), evitando duplicar em novo Salvar.
  const idTarefaEfetivo = idTarefa || idTarefaCriada;
  const isCreating = !idTarefaEfetivo;

  // 8.1.3 — Play/Stop restrito ao responsável (ou quem tem "Editar tarefas de
  // terceiros"). Se o idUsuario do logado não estiver disponível no token,
  // não bloqueia na UI (o backend valida server-side).
  const ehResponsavelDoItem =
    user?.idUsuario != null &&
    (tarefa?.responsaveis?.some((r) => r.idUsuario === user.idUsuario) ?? false);
  // FASE 04 item 2: alinhado à regra do backend — Master passa sempre e token
  // legado (sem o marcador "Tarefa.Esquema") libera.
  const podeControlarTimer =
    user?.idUsuario == null ||
    ehResponsavelDoItem ||
    havePermission(["Master"]) ||
    !havePermission(["Tarefa.Esquema"]) ||
    havePermission(["Tarefa.EditarTerceiros"]);

  // Reteste 21/08 (item 5a): espelho do check do backend (AtualizarTarefa/AtualizarSubtarefa) —
  // sem "Editar tarefas de terceiros", tarefa/sub-tarefa de terceiro fica somente leitura
  // (campos e SALVAR bloqueados) em vez de deixar editar e falhar só no salvar.
  const ehSolicitanteDoItem =
    user?.idUsuario != null &&
    !isSubtarefa &&
    tarefa?.idSolicitante === user.idUsuario;
  const podeEditarItem =
    isCreating ||
    !tarefa || // ainda carregando: não bloquear/alertar antes de conhecer o item
    user?.idUsuario == null ||
    ehResponsavelDoItem ||
    ehSolicitanteDoItem ||
    havePermission(["Master"]) ||
    !havePermission(["Tarefa.Esquema"]) ||
    havePermission(["Tarefa.EditarTerceiros"]);

  // Reteste 21/08 (item 4): sem a chave "Alocar outros usuários", o Responsável fica
  // travado (criação nasce com o usuário logado; edição mantém os atuais).
  const podeAlocarOutros =
    havePermission(["Master"]) ||
    !havePermission(["Tarefa.Esquema"]) ||
    havePermission(["Tarefa.AlocarOutros"]);

  // Tarefa/sub-tarefa concluída (status PERSISTIDO): bloqueia os campos,
  // mantendo apenas o Status editável para permitir a reabertura
  const isConcluida = !isCreating && tarefa?.idTarefaStatus === 4;

  // Gating sequencial (item 3 do relatório FASE 04): em subtarefas de tarefa de
  // tipologia, apenas a Planificação fica editável até que a Planificação tenha
  // atividade + caso + tipologia. As flags vêm do VM do backend; computamos a
  // partir das primitivas (sem depender de subtarefaEditavel) para robustez.
  const subtarefaBloqueadaGating =
    !isCreating &&
    isSubtarefa &&
    (tarefa?.ehTipologia ?? false) &&
    !(tarefa?.ehPlanificacao ?? false) &&
    !(tarefa?.planificacaoLiberada ?? false);

  // Edição efetiva: combina o gating com o modo de edição/criação. O bloqueio por
  // conclusão (isConcluida) é tratado à parte via bloquearCampos e NÃO é sobrescrito.
  const edicaoLiberada = (editMode || isCreating) && !subtarefaBloqueadaGating;

  // Dirty-state por snapshot: compara o formulário atual (normalizado) com o
  // snapshot capturado ao carregar/criar — evita falso positivo ao abrir
  // a tarefa sem editar nada
  useEffect(() => {
    setHasChanges(
      !isEqual(
        normalizeFormData(formData),
        normalizeFormData(initialFormDataRef.current)
      )
    );
  }, [formData]);

  // ============================================
  // CÁLCULO: Deve exibir aba ATIVIDADES?
  // ============================================
  // A aba ATIVIDADES só deve ser exibida quando:
  // 1. É uma sub-tarefa (isSubtarefa = true)
  // 2. O tipo de atividade é "Cadastro/edição de tipologias"
  const deveExibirAbaAtividades = useMemo(() => {
    // Verificar se é sub-tarefa
    if (!isSubtarefa) {
      return false;
    }

    // Verificar se tarefa foi carregada
    if (!tarefa) {
      return false;
    }

    // Preferir o campo ehTipologia do VM (backend); fallback para a detecção
    // por substring no nome do tipo de atividade (compatibilidade)
    const isTipoAtividadeTipologia =
      tarefa.ehTipologia ??
      (tarefa.nomeTipoAtividade
        ? tarefa.nomeTipoAtividade.toLowerCase().includes("tipologia")
        : false);

    return isTipoAtividadeTipologia;
  }, [isSubtarefa, tarefa]);

  // ============================================
  // CARREGAMENTO DE DADOS
  // ============================================

  // Carregar lista de tags da empresa
  const carregarTags = useCallback(async () => {
    // Permitir carregar tags mesmo com idEmpresa = 0 para evitar bloqueio
    // O backend retornará array vazio se não houver empresa válida
    try {
      const tagsList = await listarTags(idEmpresa);
      setTags(tagsList);
      return tagsList;
    } catch (error) {
      console.warn("Não foi possível carregar as tags:", error);
      setTags([]);
      return [];
    }
  }, [idEmpresa]);

  const carregarTarefa = useCallback(async (idOverride?: number) => {
    const idAlvo = idOverride ?? idTarefa ?? idTarefaCriada;
    if (!idAlvo) return;

    setIsLoading(true);
    try {
      // Carregar tags primeiro para poder sincronizar
      const tagsList = await carregarTags();

      // Carregar como sub-tarefa se a flag isSubtarefa estiver ativa, senão tentar como tarefa
      let data: ITarefaCompleta | null = null;
      if (isSubtarefa) {
        // Carregar diretamente como sub-tarefa
        data = await obterSubtarefaCompleta(idAlvo);
      } else {
        // Tentar como tarefa primeiro, e se não encontrar, tentar como sub-tarefa
        try {
          data = await obterTarefaCompleta(idAlvo);
        } catch (error: any) {
          // Se não encontrou como tarefa, tenta como sub-tarefa
          if (error?.response?.status === 404) {
            data = await obterSubtarefaCompleta(idAlvo);
          } else {
            throw error;
          }
        }
      }

      setTarefa(data);
      if (data) {
        // FASE 04 (item 3): sem Etapa, o título da sub-tarefa é o próprio nome.
        const tituloFormatado = data.titulo || "";

        const formInicial: Partial<IAtualizarTarefaRequest> = {
          idTarefa: data.idTarefa,
          titulo: tituloFormatado,
          descricao: data.descricao || "",
          idReferencia: data.idReferencia,
          idTipoAtividade: data.idTipoAtividade || undefined,

          idTarefaStatus: data.idTarefaStatus,
          idSolicitante: data.idSolicitante || undefined,
          nomeSolicitante: data.nomeSolicitante || undefined, // Nome completo do solicitante
          tarefaInterna: data.tarefaInterna,
          previsaoInicio: data.previsaoInicio || undefined,
          previsaoTermino: data.previsaoTermino || undefined,
          estimativaHoras: data.estimativaHoras || undefined,
          idsResponsaveis: data.responsaveis.map((r) => r.idUsuario),
          idsTags: Array.isArray(data.tags) ? data.tags.map((t) => t.idTag) : [],
        };
        setFormData(formInicial);

        // Refazer o snapshot — após carregar, não há alterações pendentes
        initialFormDataRef.current = formInicial;
        initialTagsRef.current = formInicial.idsTags || [];
        setHasChanges(false);

        // Sincronizar tags selecionadas (sub-tarefa pode não ter tags; data.tags pode ser undefined)
        const tagsDaTarefa = Array.isArray(data.tags) ? data.tags : [];
        if (tagsList && data) {
          setTagsSelecionadas(
            tagsList.filter((t) => tagsDaTarefa.some((tt) => tt.idTag === t.idTag))
          );
        }
      }

      // Verificar timer ativo
      const timer = await getTimerAtivo();
      setTimerAtivo(timer);
      // Verificar se o timer ativo pertence a esta tarefa ou sub-tarefa
      if (isSubtarefa) {
        setIsTimerRunning(timer?.idSubtarefa === idAlvo);
      } else {
        setIsTimerRunning(timer?.idTarefa === idAlvo);
      }
    } catch (error) {
      openNotification({
        isOpen: true,
        message: "Erro ao carregar tarefa",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }, [idTarefa, idTarefaCriada, isSubtarefa, openNotification]);

  useEffect(() => {
    if (open) {
      // Reset do id de criação a cada (re)abertura do modal (item 6.1)
      setIdTarefaCriada(undefined);
      if (idTarefa) {
        carregarTarefa();
        setEditMode(true); // Permitir edição ao abrir tarefa existente
      } else {
        // Modo criação
        setTarefa(null);
        const formCriacao: Partial<IAtualizarTarefaRequest> = {
          titulo: "",
          descricao: "",
          idReferencia: undefined,
          idTipoAtividade: undefined,
          idTarefaStatus: 1,
          idSolicitante: undefined,
          nomeSolicitante: undefined, // Nome completo do solicitante
          tarefaInterna: false,
          previsaoInicio: undefined,
          previsaoTermino: undefined,
          estimativaHoras: undefined,
          // Reteste 21/08 (item 4): sem a chave "Alocar outros", a tarefa nova já
          // nasce com o usuário logado como responsável (campo travado na UI).
          idsResponsaveis:
            !podeAlocarOutros && user?.idUsuario ? [user.idUsuario] : [],
          idsTags: [],
        };
        setFormData(formCriacao);
        initialFormDataRef.current = formCriacao;
        initialTagsRef.current = [];
        setEditMode(true);
        setTimerAtivo(null);
        setIsTimerRunning(false);
        setTagsSelecionadas([]);
        setTagInputValue("");
        // Carregar tags disponíveis para criação
        carregarTags();
      }
      setHasChanges(false);
      setTabAtual(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idTarefa]);

  // ============================================
  // HANDLERS
  // ============================================

  const handleFormChange = (field: string, value: any) => {
    // hasChanges é recalculado por comparação com o snapshot inicial (useEffect)
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Handlers para Tags (conforme mockup)
  const handleAddTag = (tag: ITag | null) => {
    if (tag && !tagsSelecionadas.some((t) => t.idTag === tag.idTag)) {
      const novasTags = [...tagsSelecionadas, tag];
      setTagsSelecionadas(novasTags);
      handleFormChange("idsTags", novasTags.map((t) => t.idTag));
    }
    setTagInputValue("");
  };

  const handleRemoveTag = (idTag: number) => {
    const novasTags = tagsSelecionadas.filter((t) => t.idTag !== idTag);
    setTagsSelecionadas(novasTags);
    handleFormChange("idsTags", novasTags.map((t) => t.idTag));
  };

  const handleCriarNovaTag = async () => {
    // Capturar o valor atual do campo antes de qualquer operação assíncrona
    const nomeTag = tagInputValue.trim();
    
    // Validação: campo vazio
    if (!nomeTag) {
      openNotification({
        isOpen: true,
        message: "Digite um nome para a tag antes de incluir",
        type: "warning",
      });
      return;
    }

    // Validação: já está criando
    if (criandoTag) {
      return;
    }
    
    // Validação: empresa não identificada
    if (!idEmpresa || idEmpresa <= 0) {
      openNotification({
        isOpen: true,
        message: "Não foi possível criar a tag: sessão expirada ou usuário não autenticado. Por favor, faça login novamente.",
        type: "error",
      });
      return;
    }
    
    try {
      setCriandoTag(true);
      
      const novaTag = await criarOuObterTag({
        idEmpresa,
        nome: nomeTag,
        cor: "#3C90AD",
      });
      
      if (novaTag) {
        // Adicionar à lista de tags disponíveis (evitar duplicados)
        setTags((prev) => {
          const existe = prev.some((t) => t.idTag === novaTag.idTag);
          return existe ? prev : [...prev, novaTag];
        });
        handleAddTag(novaTag);
        setTagInputValue(""); // Limpar campo após sucesso
        openNotification({
          isOpen: true,
          message: `Tag "${nomeTag}" criada com sucesso!`,
          type: "success",
        });
      }
    } catch (error: any) {
      console.error("Erro ao criar tag:", error);
      // Mensagem mais amigável para o usuário
      const mensagemErro = error?.message?.includes("IdEmpresa")
        ? "Sessão expirada. Por favor, faça login novamente."
        : error?.message || "Não foi possível criar a tag. Tente novamente.";
      openNotification({
        isOpen: true,
        message: mensagemErro,
        type: "error",
      });
    } finally {
      setCriandoTag(false);
    }
  };

  // Verificar se existe tag com o texto digitado
  const tagExiste = tags.some(
    (t) => t.nome.toLowerCase() === tagInputValue.toLowerCase()
  );

  const handleToggleTarefaInterna = () => {
    if (editMode && !isConcluida) {
      handleFormChange("tarefaInterna", !formData.tarefaInterna);
    }
  };

  const handleToggleTimer = async () => {
    if (!tarefa) return;

    setIsSaving(true);
    try {
      if (isTimerRunning && timerAtivo) {
        await pararTimer({ idTarefaTimer: timerAtivo.idTarefaTimer });
        openNotification({
          isOpen: true,
          message: "Timer parado!",
          type: "success",
        });
      } else {
        // Para sub-tarefas, enviar idSubtarefa; para tarefas, enviar idTarefa
        if (isSubtarefa) {
          await iniciarTimer({ idSubtarefa: tarefa.idTarefa });
        } else {
          await iniciarTimer({ idTarefa: tarefa.idTarefa });
        }
        setIsTimerRunning(true);
        const timer = await getTimerAtivo();
        setTimerAtivo(timer);
        openNotification({
          isOpen: true,
          message: "Timer iniciado!",
          type: "success",
        });
      }
      await carregarTarefa();
      if (isTimerRunning && timerAtivo) {
        setIsTimerRunning(false);
        setTimerAtivo(null);
        setApontamentoRefreshKey((k) => k + 1);
      }
      // Reteste 21/08 (item 6): propagar o refresh para o quadro — era o único fluxo
      // do modal sem onSave(), e o card ficava com o ícone Play desatualizado.
      onSave();
    } catch (error: any) {
      console.error("[ModalTarefaDetalhe] Erro ao controlar timer:", error);

      // Exibir a mensagem real do backend: o service (iniciarTimer/pararTimer)
      // passa pelo getErrorsByApi, que lança Error com as mensagens do
      // CustomResponse (HTTP 400) em error.message
      const mensagemErro = error?.message || "Erro ao controlar timer";
      openNotification({
        isOpen: true,
        message: mensagemErro,
        type: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleConcluir = async () => {
    if (!tarefa) return;

    // CORREÇÃO: Passar status 4 diretamente na request para evitar race condition
    // Não depender do setFormData que é assíncrono
    setIsSaving(true);
    try {
      if (isSubtarefa) {
        const nomeSubtarefa = formData.titulo || "";

        const subtarefaRequest: IAtualizarSubtarefaRequest = {
          idSubtarefa: tarefa.idTarefa, // O idTarefa na verdade é o idSubtarefa quando isSubtarefa=true
          nome: nomeSubtarefa,
          descricao: formData.descricao || undefined,
          idTarefaStatus: 4, // CORREÇÃO: Status 4 (Concluída) passado diretamente
          previsaoInicio: formData.previsaoInicio,
          previsaoTermino: formData.previsaoTermino,
          estimativaHoras: formData.estimativaHoras,
          idsResponsaveis: formData.idsResponsaveis || [],
          idsTags: formData.idsTags || [],
        };

        await atualizarSubtarefa(subtarefaRequest);
        // Atualiza o estado visual sem marcar dirty (o snapshot é refeito no carregarTarefa)
        setFormData((prev) => ({ ...prev, idTarefaStatus: 4 }));
        openNotification({
          isOpen: true,
          message: "Sub-tarefa concluída com sucesso!",
          type: "success",
        });
        await carregarTarefa(); // Recarrega para refletir mudanças
        if (onSave) onSave();
      } else {
        // Para tarefas principais, usar endpoint de atualização de tarefa
        const tarefaRequest: IAtualizarTarefaRequest = {
          idTarefa: formData.idTarefa!,
          titulo: formData.titulo!,
          descricao: formData.descricao || undefined,
          idReferencia: formData.idReferencia!,
          idTipoAtividade: formData.idTipoAtividade,
          idSolicitante: formData.idSolicitante,
          nomeSolicitante: formData.nomeSolicitante,
          tarefaInterna: formData.tarefaInterna || false,
          previsaoInicio: formData.previsaoInicio,
          previsaoTermino: formData.previsaoTermino,
          estimativaHoras: formData.estimativaHoras,
          idTarefaStatus: 4, // CORREÇÃO: Status 4 (Concluída) passado diretamente
          idsResponsaveis: formData.idsResponsaveis || [],
          idsTags: formData.idsTags || [],
        };

        await atualizarTarefa(tarefaRequest);
        // Atualiza o estado visual sem marcar dirty (o snapshot é refeito no carregarTarefa)
        setFormData((prev) => ({ ...prev, idTarefaStatus: 4 }));
        openNotification({
          isOpen: true,
          message: "Tarefa concluída com sucesso!",
          type: "success",
        });
        await carregarTarefa(); // Recarrega para refletir mudanças
        if (onSave) onSave();
      }
    } catch (error: any) {
      console.error("[ModalTarefaDetalhe] Erro ao concluir:", error);
      openNotification({
        isOpen: true,
        message: isSubtarefa ? "Erro ao concluir sub-tarefa" : "Erro ao concluir tarefa",
        type: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    if (!formData.titulo?.trim()) {
      openNotification({
        isOpen: true,
        message: isSubtarefa ? "O nome da sub-tarefa é obrigatório" : "O título é obrigatório",
        type: "warning",
      });
      return;
    }

    // Referência só é obrigatória para tarefas principais, não para sub-tarefas
    if (!isSubtarefa && !formData.idReferencia) {
      openNotification({
        isOpen: true,
        message: "A referência é obrigatória",
        type: "warning",
      });
      return;
    }

    // Verificar se há gatilho para o tipo de atividade selecionado
    // Isso deve acontecer na CRIAÇÃO ou quando o tipo de atividade é ALTERADO na edição
    const idTipoAtividadeNum = formData.idTipoAtividade 
      ? (typeof formData.idTipoAtividade === 'string' 
          ? parseInt(formData.idTipoAtividade, 10) 
          : Number(formData.idTipoAtividade))
      : null;

    // Verificar se o tipo de atividade foi alterado (na edição)
    const tipoAtividadeOriginal = tarefa?.idTipoAtividade || null;
    const tipoAtividadeFoiAlterado = !isCreating && idTipoAtividadeNum !== tipoAtividadeOriginal;

    // Verificar gatilho se:
    // 1. É criação de nova tarefa COM tipo de atividade selecionado, OU
    // 2. É edição E o tipo de atividade foi ALTERADO para um novo valor
    const deveVerificarGatilho = (isCreating || tipoAtividadeFoiAlterado) && 
                                  idTipoAtividadeNum && 
                                  idTipoAtividadeNum > 0 && 
                                  idEmpresa > 0 &&
                                  !isSubtarefa; // Não verificar gatilho para sub-tarefas

    if (deveVerificarGatilho) {
      try {
        const gatilho = await getGatilhoPorTipoAtividade(idEmpresa, idTipoAtividadeNum);

        if (gatilho && gatilho.subtarefas && Array.isArray(gatilho.subtarefas) && gatilho.subtarefas.length > 0) {
          // Há gatilho com sub-tarefas - exibir modal de confirmação
          setGatilhoEncontrado(gatilho);
          // Capturar o gatilho no closure para garantir que estará disponível quando executarSalvamento for chamado
          setPendingSave(() => async () => {
            // Usar o gatilho capturado diretamente, não do estado
            await executarSalvamentoComGatilho(gatilho);
          });
          setModalConfirmarSubtarefasOpen(true);
          return; // Não salva ainda, aguarda confirmação
        }
      } catch (error) {
        console.error("[ModalTarefaDetalhe] ❌ Erro ao buscar gatilho:", error);
        // Em caso de erro, continua com o salvamento normal
      }
    }

    // Se não há gatilho ou é edição, salva normalmente
    await executarSalvamento();
  };

  // Função auxiliar para executar salvamento com gatilho específico
  const executarSalvamentoComGatilho = async (gatilho: IEmpresaTarefaGatilho | null) => {
    setIsSaving(true);
    try {
      if (isCreating) {
        // Preparar sub-tarefas do gatilho se houver
        const subtarefasDoGatilho = gatilho?.subtarefas?.map((st, index) => {
          return {
            nome: st.nome,
            descricao: "",
            previsaoInicio: undefined,
            previsaoTermino: undefined,
            estimativaHoras: undefined,
            ordem: index + 1,
            idsResponsaveis: [],
          };
        }) || [];

        const createRequest: ICriarTarefaRequest = {
          titulo: formData.titulo!,
          descricao: formData.descricao || undefined,
          idReferencia: formData.idReferencia!,
          idTipoAtividade: formData.idTipoAtividade,
          idEmpresa: idEmpresa,
          idSolicitante: formData.idSolicitante,
          nomeSolicitante: formData.nomeSolicitante,
          tarefaInterna: formData.tarefaInterna || false,
          previsaoInicio: formData.previsaoInicio,
          previsaoTermino: formData.previsaoTermino,
          estimativaHoras: formData.estimativaHoras,
          idsResponsaveis: formData.idsResponsaveis || [],
          subtarefas: subtarefasDoGatilho,
        };

        const result = await criarTarefa(createRequest);

        if (result) {
          openNotification({
            isOpen: true,
            message: `Tarefa #${result.idTarefa} criada com sucesso!`,
            type: "success",
          });

          // Item 6.1: não fechar o modal ao Salvar. Migra para modo edição
          // usando o id retornado (evita duplicar em novo Salvar) e recarrega.
          onSave();
          setIdTarefaCriada(result.idTarefa);
          setEditMode(true);
          await carregarTarefa(result.idTarefa);
        }
      } else {
        if (!tarefa) return;

        // Verificar se é sub-tarefa ou tarefa principal
        if (isSubtarefa) {
          // Para sub-tarefas, usar endpoint específico
          const nomeSubtarefa = formData.titulo || "";

          const subtarefaRequest: IAtualizarSubtarefaRequest = {
            idSubtarefa: tarefa.idTarefa, // O idTarefa na verdade é o idSubtarefa quando isSubtarefa=true
            nome: nomeSubtarefa,
            descricao: formData.descricao || undefined,
            idTarefaStatus: formData.idTarefaStatus || tarefa.idTarefaStatus,
            previsaoInicio: formData.previsaoInicio,
            previsaoTermino: formData.previsaoTermino,
            estimativaHoras: formData.estimativaHoras,
            idsResponsaveis: formData.idsResponsaveis || [],
            idsTags: formData.idsTags || [],
          };

          await atualizarSubtarefa(subtarefaRequest);
          openNotification({
            isOpen: true,
            message: "Sub-tarefa atualizada com sucesso!",
            type: "success",
          });
        } else {
          // Para tarefas principais, usar endpoint original
          if (!formData.idTarefa) {
            return;
          }

          // Primeiro, atualizar a tarefa
          await atualizarTarefa(formData as IAtualizarTarefaRequest);

          // Se há gatilho com sub-tarefas, criar as sub-tarefas após atualizar a tarefa
          if (gatilho && gatilho.subtarefas && Array.isArray(gatilho.subtarefas) && gatilho.subtarefas.length > 0) {
            // Criar cada sub-tarefa do gatilho
            for (let index = 0; index < gatilho.subtarefas.length; index++) {
              const st = gatilho.subtarefas[index];
              const subtarefaRequest: ICriarSubtarefaDetalheRequest = {
                idTarefa: formData.idTarefa,
                nome: st.nome,
                descricao: "",
                previsaoInicio: undefined,
                previsaoTermino: undefined,
                estimativaHoras: undefined,
                ordem: index + 1,
                idsResponsaveis: [],
              };

              await criarSubtarefa(subtarefaRequest);
            }
            
            openNotification({
              isOpen: true,
              message: `Tarefa atualizada com sucesso! ${gatilho.subtarefas.length} sub-tarefa(s) criada(s).`,
              type: "success",
            });
          } else {
            openNotification({
              isOpen: true,
              message: "Tarefa atualizada com sucesso!",
              type: "success",
            });
          }
        }
        // FASE 04 — relatório de 19/08 (item 6): recarregar após salvar refaz o
        // snapshot do formulário e zera o dirty-state. Sem isto, "Existem
        // alterações não salvas" continuava aparecendo ao Cancelar depois de um
        // Salvar bem-sucedido. Os ramos de criação e de handleConcluir já
        // recarregavam — só a edição não.
        await carregarTarefa();
        // Item 6.1: não fechar o modal ao Salvar — mantém aberto em modo edição.
        onSave();
      }
    } catch (error: any) {
      console.error("[ModalTarefaDetalhe] Erro ao executar salvamento:", error);
      let mensagemErro = "Erro ao salvar tarefa";
      if (isCreating) {
        mensagemErro = "Erro ao criar tarefa";
      } else if (isSubtarefa) {
        mensagemErro = "Erro ao salvar sub-tarefa";
      }
      // Reteste 21/08 (item 3): a API devolve o motivo real ("Usuário sem permissão: ...");
      // exibi-lo em vez do texto genérico — sem isso não dá para saber qual chave barrou.
      // Demais erros mantêm a mensagem genérica (evita vazar "Network Error" etc. na tela).
      if (error?.message?.includes("Usuário sem permissão")) {
        const motivo = error.message.replace("Usuário sem permissão:", "").trim();
        mensagemErro = `Sem permissão, contate o administrador — ${motivo}`;
      }
      openNotification({
        isOpen: true,
        message: mensagemErro,
        type: "error",
      });
    } finally {
      setIsSaving(false);
      setModalConfirmarSubtarefasOpen(false);
      setGatilhoEncontrado(null);
      setPendingSave(null);
    }
  };

  // Função original mantida para compatibilidade (quando não há gatilho)
  const executarSalvamento = async () => {
    await executarSalvamentoComGatilho(gatilhoEncontrado);
  };

  const handleConfirmarSubtarefas = async () => {
    if (pendingSave) {
      await pendingSave();
    }
  };

  const handleCancelarSubtarefas = () => {
    // Ao clicar em NÃO, não salva a tarefa e mantém a tela aberta
    setModalConfirmarSubtarefasOpen(false);
    setGatilhoEncontrado(null);
    setPendingSave(null);
    openNotification({
      isOpen: true,
      message: "A criação de sub-tarefas é obrigatória para este tipo de atividade. A tarefa não foi salva.",
      type: "warning",
    });
  };

  const handleDuplicate = async () => {
    if (!tarefa) return;

    if (!window.confirm("Deseja duplicar esta tarefa?")) return;

    setIsSaving(true);
    try {
      const result = await duplicarTarefa({
        idTarefa: tarefa.idTarefa,
        copiarSubtarefas: true,
        copiarResponsaveis: true,
        copiarTags: true,
      });
      if (result) {
        openNotification({
          isOpen: true,
          message: `Tarefa duplicada! Nova tarefa: #${result.idTarefa}`,
          type: "success",
        });
        onSave();
      }
    } catch (error) {
      openNotification({
        isOpen: true,
        message: "Erro ao duplicar tarefa",
        type: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!tarefa) return;

    const mensagemConfirmacao = isSubtarefa
      ? "Tem certeza que deseja excluir esta sub-tarefa? Esta ação não pode ser desfeita."
      : "Tem certeza que deseja excluir esta tarefa? Esta ação não pode ser desfeita.";

    if (!window.confirm(mensagemConfirmacao)) return;

    setIsSaving(true);
    try {
      if (isSubtarefa) {
        // Para sub-tarefas, usar endpoint específico
        await excluirSubtarefa(tarefa.idTarefa);
        openNotification({
          isOpen: true,
          message: "Sub-tarefa excluída com sucesso!",
          type: "success",
        });
      } else {
        // Para tarefas principais
        await excluirTarefa(tarefa.idTarefa);
        openNotification({
          isOpen: true,
          message: "Tarefa excluída com sucesso!",
          type: "success",
        });
      }
      onSave();
      onClose();
    } catch (error) {
      openNotification({
        isOpen: true,
        message: isSubtarefa ? "Erro ao excluir sub-tarefa" : "Erro ao excluir tarefa",
        type: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (hasChanges) {
      if (
        !window.confirm("Existem alterações não salvas. Deseja sair sem salvar?")
      ) {
        return;
      }
    }
    onClose();
  };

  // Função para abrir sub-tarefa em modal separada
  const handleOpenSubtarefa = (idSubtarefa: number) => {
    setSubtarefaSelecionadaId(idSubtarefa);
    setModalSubtarefaAberto(true);
  };

  // Função para fechar modal de sub-tarefa
  const handleCloseModalSubtarefa = () => {
    setModalSubtarefaAberto(false);
    setSubtarefaSelecionadaId(null);
    // Recarregar tarefa principal para atualizar dados
    if (idTarefa) {
      carregarTarefa();
    }
    // Propagar o refresh até o quadro (status/timer podem ter mudado)
    onSave();
  };

  // Refresh do painel de sub-tarefas propagado até o quadro
  // (ex.: concluir sub-tarefa deve atualizar o card e o indicador de timer)
  const handleRefreshSubtarefas = async () => {
    await carregarTarefa();
    onSave();
  };

  // Formatar número da tarefa (5 dígitos conforme mockup)
  const formatarNumeroTarefa = (numero: number): string => {
    return numero.toString().padStart(5, "0");
  };

  // ============================================
  // RENDER
  // ============================================

  return (
    <>
      <BackdropLoading isOpen={isSaving} />

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            height: "90vh",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* ====== HEADER CONFORME MOCKUP ====== */}
        <HeaderContainer>
          {/* Linha única: Número + Botões + Título + TAREFA INTERNA (todos alinhados) */}
          <Box display="flex" alignItems="center" width="100%" gap={2}>
            {/* Número da Tarefa/Sub-tarefa */}
            {isCreating ? (
              <TarefaNumero>Nova Tarefa</TarefaNumero>
            ) : tarefa ? (
              <TarefaNumero>
                {isSubtarefa 
                  ? `Sub-Tarefa - ${formatarNumeroTarefa(tarefa.numero)}`
                  : `Tarefa - ${formatarNumeroTarefa(tarefa.numero)}`
                }
              </TarefaNumero>
            ) : null}

            {/* Botões de ação - ao lado do número */}
            {/* Para tarefas principais: só exibir quando NÃO possui sub-tarefas */}
            {/* Para sub-tarefas: sempre exibir */}
            {/* Exceção: exibir o Stop quando o timer ativo pertence à própria tarefa,
                mesmo com sub-tarefas (timer iniciado no pai antes de criar sub-tarefas) */}
            {!isCreating && tarefa && (
              (isSubtarefa ||
                !tarefa.subtarefas ||
                tarefa.subtarefas.length === 0 ||
                (timerAtivo?.idTarefa === tarefa.idTarefa && isTimerRunning)) && (
                <Box display="flex" gap={1}>
                  {/* Botão Concluir (✓ verde) - Fundo verde com ícone branco conforme mockup */}
                  {/* Não exibir quando o bloco está visível apenas pela exceção do timer do pai */}
                  {(isSubtarefa || !tarefa.subtarefas || tarefa.subtarefas.length === 0) && (
                    <Tooltip title="Marcar como Concluída">
                      <ActionButton
                        bgcolor="#4caf50"
                        onClick={handleConcluir}
                        disabled={tarefa.idTarefaStatus === 4}
                        sx={{
                          backgroundColor: "#4caf50 !important", // Verde conforme mockup
                          "& svg": {
                            color: "#fff !important", // Ícone branco conforme mockup
                          },
                          "&:hover": {
                            backgroundColor: "#45a049 !important", // Verde mais escuro no hover
                          },
                          "&:disabled": {
                            backgroundColor: "#81c784 !important", // Verde mais claro quando desabilitado
                            opacity: 0.6,
                          },
                        }}
                      >
                        <Check fontSize="small" sx={{ color: "#fff !important" }} />
                      </ActionButton>
                    </Tooltip>
                  )}

                  {/* Botão Play/Stop Timer (▶ azul) */}
                  <Tooltip
                    title={
                      !podeControlarTimer
                        ? "Apenas o responsável pode controlar o timer"
                        : isTimerRunning
                        ? "Parar Timer"
                        : "Iniciar Timer"
                    }
                  >
                    <ActionButton
                      bgcolor={isTimerRunning ? "#f44336" : "#2196f3"}
                      onClick={handleToggleTimer}
                      disabled={tarefa.idTarefaStatus === 4 || !podeControlarTimer}
                      sx={{
                        "&:disabled": {
                          backgroundColor: "#90caf9 !important", // Azul claro quando desabilitado
                          opacity: 0.6,
                        },
                      }}
                    >
                      {isTimerRunning ? (
                        <Stop fontSize="small" />
                      ) : (
                        <PlayArrow fontSize="small" />
                      )}
                    </ActionButton>
                  </Tooltip>
                </Box>
              )
            )}

            {/* Campo de Título Editável */}
            <Box flex={1}>
              {(
                <TituloInput
                  placeholder={isSubtarefa ? "Digite o nome da sub-tarefa..." : "Digite o título da tarefa..."}
                  value={formData.titulo || ""}
                  onChange={(e) => handleFormChange("titulo", e.target.value)}
                  disabled={(!editMode && !isCreating) || isConcluida || subtarefaBloqueadaGating}
                  fullWidth
                />
              )}
            </Box>

            {/* Toggle Tarefa Interna */}
            <TarefaInternaToggle
              onClick={handleToggleTarefaInterna}
              sx={{ cursor: editMode || isCreating ? "pointer" : "default" }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: formData.tarefaInterna ? "primary.main" : "text.secondary",
                }}
              >
                TAREFA INTERNA
              </Typography>
              <Switch
                checked={formData.tarefaInterna || false}
                onChange={handleToggleTarefaInterna}
                size="small"
                disabled={(!editMode && !isCreating) || isConcluida}
              />
            </TarefaInternaToggle>
          </Box>
        </HeaderContainer>

        {/* ====== CONTEÚDO ====== */}
        <DialogContent sx={{ p: 0, display: "flex", overflow: "auto", flex: 1 }}>
          {isLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              width="100%"
            >
              <Typography>Carregando...</Typography>
            </Box>
          ) : isCreating || tarefa ? (
            <>
              {/* Área principal */}
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "auto",
                }}
              >
                {/* Linha: Campo de Tags (esquerda) + Abas (direita) - Conforme Mockup */}
                <TagsAndTabsRow>
                  {/* Campo de Tags com ícone - Conforme Mockup */}
                  <Box display="flex" alignItems="center" gap={1} flex={1} flexWrap="wrap">
                    {(editMode || isCreating) && !isConcluida && (
                      <>
                        {/* Campo Nova Tag com autocomplete para pesquisa e criação */}
                        <Autocomplete
                          freeSolo
                          options={tags.filter(
                            (t) => !tagsSelecionadas.some((ts) => ts.idTag === t.idTag)
                          )}
                          getOptionLabel={(option) => {
                            if (typeof option === "string") return option;
                            return option.nome;
                          }}
                          inputValue={tagInputValue}
                          onInputChange={(_, newValue) => {
                            setTagInputValue(newValue);
                          }}
                          onChange={(_, value) => {
                            if (value && typeof value !== "string") {
                              // Tag existente selecionada
                              handleAddTag(value);
                              setTagInputValue(""); // Limpar campo após selecionar
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Nova Tag"
                              size="small"
                              InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                  <>
                                    <InputAdornment position="start">
                                      <LocalOffer
                                        fontSize="small"
                                        sx={{ color: "#3C90AD" }}
                                      />
                                    </InputAdornment>
                                    {params.InputProps.startAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                          size="small"
                          sx={{ minWidth: 180, maxWidth: 220 }}
                        />
                        
                        {/* Botão Incluir Tag - Conforme Mockup */}
                        {/* Sempre visível, desabilitado quando: sem texto, empresa inválida, ou tag já existe */}
                        {(() => {
                          // Determinar razão do disabled para mostrar tooltip informativo
                          const isDisabled = criandoTag || !idEmpresa || !tagInputValue.trim() || tagExiste;
                          let tooltipMessage = "";
                          if (!idEmpresa || idEmpresa <= 0) {
                            tooltipMessage = "Sessão expirada. Faça login novamente.";
                          } else if (!tagInputValue.trim()) {
                            tooltipMessage = "Digite o nome da tag";
                          } else if (tagExiste) {
                            tooltipMessage = "Selecione a tag da lista";
                          } else if (criandoTag) {
                            tooltipMessage = "Criando...";
                          }
                          
                          return (
                            <Tooltip title={isDisabled ? tooltipMessage : "Criar nova tag"}>
                              <span>
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={handleCriarNovaTag}
                                  disabled={isDisabled}
                                  sx={{
                                    backgroundColor: "#3C90AD",
                                    textTransform: "uppercase",
                                    whiteSpace: "nowrap",
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                    "&:hover": {
                                      backgroundColor: "#2d7a94",
                                    },
                                    "&:disabled": {
                                      backgroundColor: "#9e9e9e",
                                      color: "#fff",
                                    },
                                  }}
                                >
                                  {criandoTag ? "..." : "Incluir Tag"}
                                </Button>
                              </span>
                            </Tooltip>
                          );
                        })()}
                      </>
                    )}

                    {/* Tags selecionadas */}
                    {tagsSelecionadas.map((tag) => (
                      <Chip
                        key={tag.idTag}
                        label={tag.nome}
                        size="small"
                        onDelete={((editMode || isCreating) && !isConcluida) ? () => handleRemoveTag(tag.idTag) : undefined}
                        sx={{
                          backgroundColor: tag.cor || "#3C90AD",
                          color: "#fff",
                          fontWeight: 500,
                          fontSize: "0.75rem",
                        }}
                      />
                    ))}
                  </Box>

                  {/* Abas como botões - Alinhadas à direita */}
                  <TabsButtonGroup>
                    <TabButton
                      active={tabAtual === 0}
                      onClick={() => setTabAtual(0)}
                    >
                      DETALHES
                    </TabButton>
                    {/* Aba ATIVIDADES - Apenas para sub-tarefas do tipo "Cadastro/edição de tipologias" */}
                    {!isCreating && deveExibirAbaAtividades && tarefa && (
                      <TabButton
                        active={tabAtual === 1}
                        onClick={() => setTabAtual(1)}
                      >
                        ATIVIDADES
                      </TabButton>
                    )}
                    {!isCreating && (
                      <TabButton
                        active={tabAtual === (deveExibirAbaAtividades ? 2 : 1)}
                        onClick={() => setTabAtual(deveExibirAbaAtividades ? 2 : 1)}
                      >
                        APONTAMENTO HORAS
                      </TabButton>
                    )}
                    {!isCreating && (
                      <TabButton
                        active={tabAtual === (deveExibirAbaAtividades ? 3 : 2)}
                        onClick={() => setTabAtual(deveExibirAbaAtividades ? 3 : 2)}
                      >
                        DOCUMENTOS
                      </TabButton>
                    )}
                  </TabsButtonGroup>
                </TagsAndTabsRow>

                {/* Conteúdo das abas */}
                <Box sx={{ flex: 1, overflow: "auto", minHeight: 300 }}>
                  <TabPanel value={tabAtual} index={0}>
                    {subtarefaBloqueadaGating && (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Conclua a Planificação (atividade + caso + tipologia) para
                        liberar esta etapa.
                      </Alert>
                    )}
                    {!podeEditarItem && (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Sem permissão para editar tarefas de terceiros — somente
                        leitura.
                      </Alert>
                    )}
                    <TabDetalhes
                      tarefa={tarefa}
                      formData={formData}
                      editMode={edicaoLiberada}
                      idEmpresa={idEmpresa}
                      onChange={handleFormChange}
                      onRefresh={carregarTarefa}
                      isCreating={isCreating}
                      isSubtarefa={isSubtarefa}
                      bloquearCampos={isConcluida || !podeEditarItem}
                      podeAlocarOutros={podeAlocarOutros}
                    />
                  </TabPanel>

                  {!isCreating && tarefa && (
                    <>
                      {/* Aba ATIVIDADES - index 1 quando deveExibirAbaAtividades é true */}
                      {deveExibirAbaAtividades && (
                        <TabPanel value={tabAtual} index={1}>
                          <TabAtividades
                            idTarefa={tarefa.idTarefa}
                            editMode={editMode || isCreating}
                            ehPlanificacao={tarefa.ehPlanificacao}
                            idReferencia={tarefa.idReferencia}
                          />
                        </TabPanel>
                      )}

                      <TabPanel value={tabAtual} index={deveExibirAbaAtividades ? 2 : 1}>
                        <TabApontamentoHoras
                          key={apontamentoRefreshKey}
                          idTarefa={tarefa.idTarefa}
                          subtarefas={tarefa.subtarefas}
                          onRefresh={carregarTarefa}
                          isSubtarefa={isSubtarefa}
                        />
                      </TabPanel>

                      <TabPanel value={tabAtual} index={deveExibirAbaAtividades ? 3 : 2}>
                        <TabDocumentos
                          idTarefa={tarefa.idTarefa}
                          subtarefas={tarefa.subtarefas}
                          onRefresh={carregarTarefa}
                          isSubtarefa={isSubtarefa}
                        />
                      </TabPanel>
                    </>
                  )}
                </Box>
              </Box>

              {/* Painel lateral direito - Sempre visível (exceto criação) */}
              {!isCreating && tarefa && (
                <PainelLateral>
                  {/* Abas do painel lateral - conforme mockup */}
                  {/* Sub-tarefas não têm sub-tarefas aninhadas, então esconde a aba "LISTA DE TAREFAS" */}
                  <PainelLateralTabsContainer>
                    {!isSubtarefa && (
                      <PainelLateralTab
                        active={tabPainelLateral === 0}
                        onClick={() => setTabPainelLateral(0)}
                      >
                        LISTA DE TAREFAS
                      </PainelLateralTab>
                    )}
                    <PainelLateralTab
                      active={isSubtarefa ? true : tabPainelLateral === 1}
                      onClick={() => setTabPainelLateral(1)}
                    >
                      COMENTÁRIOS
                    </PainelLateralTab>
                  </PainelLateralTabsContainer>

                  {/* Conteúdo do painel lateral */}
                  <Box sx={{ flex: 1, overflow: "auto" }}>
                    {/* Sub-tarefas mostram apenas Comentários (não têm sub-tarefas aninhadas) */}
                    {!isSubtarefa && tabPainelLateral === 0 ? (
                      <PainelSubtarefas
                        idTarefa={tarefa.idTarefa}
                        subtarefas={tarefa.subtarefas}
                        onRefresh={handleRefreshSubtarefas}
                        onOpenSubtarefa={handleOpenSubtarefa}
                        idReferencia={tarefa.idReferencia}
                        ehTipologia={tarefa?.ehTipologia}
                      />
                    ) : (
                      <PainelComentarios
                        idTarefa={tarefa.idTarefa}
                        onRefresh={carregarTarefa}
                        isSubtarefa={isSubtarefa}
                        idReferencia={tarefa.idReferencia}
                      />
                    )}
                  </Box>
                </PainelLateral>
              )}
            </>
          ) : (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              width="100%"
            >
              <Typography color="textSecondary">Tarefa não encontrada</Typography>
            </Box>
          )}
        </DialogContent>

        {/* ====== FOOTER CONFORME MOCKUP ====== */}
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: 1,
            borderColor: "divider",
            justifyContent: "flex-end",
            gap: 1,
          }}
        >
          <Button onClick={handleClose} variant="outlined" color="inherit">
            CANCELAR
          </Button>

          {!isCreating && tarefa && (
            <>
              <Button
                variant="outlined"
                color="error"
                onClick={handleDelete}
                disabled={isSaving}
              >
                EXCLUIR
              </Button>
              {/* Duplicar não está disponível para sub-tarefas */}
              {!isSubtarefa && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleDuplicate}
                  disabled={isSaving}
                >
                  DUPLICAR
                </Button>
              )}
            </>
          )}

          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={
              (!hasChanges && !isCreating) ||
              !formData.titulo?.trim() ||
              (!isSubtarefa && !formData.idReferencia) ||
              isSaving ||
              subtarefaBloqueadaGating ||
              !podeEditarItem
            }
            sx={{ minWidth: 120 }}
          >
            SALVAR
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de confirmação de criação automática de sub-tarefas */}
      <ModalConfirmarSubtarefas
        open={modalConfirmarSubtarefasOpen}
        onClose={handleCancelarSubtarefas}
        onConfirm={handleConfirmarSubtarefas}
        gatilho={gatilhoEncontrado}
      />

      {/* Modal de Sub-tarefa - Abre sub-tarefa em modal separada */}
      {modalSubtarefaAberto && subtarefaSelecionadaId && (
        <ModalTarefaDetalhe
          open={modalSubtarefaAberto}
          idTarefa={subtarefaSelecionadaId}
          isSubtarefa={true}
          onClose={handleCloseModalSubtarefa}
          onSave={() => {
            // Item 6.1: NÃO fechar o modal da subtarefa ao Salvar/Concluir —
            // apenas atualizar a tarefa pai. Fechamento só pelo X/Cancelar.
            if (idTarefa) {
              carregarTarefa();
            }
          }}
        />
      )}

    </>
  );
};
